import React from 'react';
import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { AlertCircle, Save } from 'react-feather';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import ClientPageCardHeader from 'ts/common/components/ClientPageCardHeader';
import { useTranslate } from 'ts/common/hooks';
import { formatDate } from 'ts/common/questionnaire/common';
import type { QuestionnaireContext } from 'client_react/questionnaire/QuestionnaireApp';
import QuestionnaireEmailForm from 'client_react/questionnaire/components/QuestionnaireEmailForm';
import QuestionnaireQuestionsForm from 'client_react/questionnaire/components/QuestionnaireQuestionsForm';

const QuestionnairePage: React.FC = () => {
    const t = useTranslate('clientQuestionnaire');
    const isLargeScreen = useBreakpointValue({ base: false, lg: true });

    const { questionnaire, saving, savingFailed } = useOutletContext<QuestionnaireContext>();
    const [searchParams, setSearchParams] = useSearchParams();

    // TODO: Get default page index from API based on last page with a response
    const defaultPageIndex = 0;
    let pageIndex = searchParams.has('page')
        ? Number(searchParams.get('page')) - 1
        : defaultPageIndex;

    const sortedPages =
        'pages' in questionnaire
            ? [...questionnaire.pages].sort((a, b) => a.displayOrder - b.displayOrder)
            : undefined;

    if (!sortedPages?.[pageIndex]) {
        pageIndex = defaultPageIndex;
    }

    if (!('pages' in questionnaire)) {
        return <QuestionnaireEmailForm />;
    }

    return (
        <Flex direction="column" gap="24px" marginBottom="24px">
            <ClientPageCardHeader
                headingText={questionnaire.name}
                statusText={t(`statuses.${questionnaire.questionnaireStatus}`)}
            />
            <Flex flexDirection="row" justifyContent="space-between" alignItems="flex-start">
                <Flex flexDirection="column" gap="8px">
                    <Flex
                        as="dl"
                        margin={0}
                        fontSize="14px"
                        flexDirection="row"
                        flexWrap="wrap"
                        gap={{ base: '40px', md: '80px' }}
                        alignItems="flex-start"
                    >
                        <Flex flexDirection="column" gap="4px">
                            <Text as="dt" fontWeight="600" marginBottom={0}>
                                {t('fromLabel')}
                            </Text>
                            <Text as="dd">{questionnaire.brandName}</Text>
                        </Flex>
                        <Flex flexDirection="column" gap="4px">
                            <Text as="dt" fontWeight="600" marginBottom={0}>
                                {t('toLabel')}
                            </Text>
                            <Text as="dd">{questionnaire.contact.name}</Text>
                        </Flex>
                        <Flex flexDirection="column" gap="4px">
                            <Text as="dt" fontWeight="600" marginBottom={0}>
                                {t('dueDateLabel')}
                            </Text>
                            <Text as="dd">
                                {questionnaire.dueDate
                                    ? formatDate(questionnaire.dueDate, 'UTC')
                                    : '-'}
                            </Text>
                        </Flex>
                    </Flex>
                    {questionnaire.descriptionHtml && (
                        <Box
                            fontFamily="open-sans"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(questionnaire.descriptionHtml)
                            }}
                        />
                    )}
                </Flex>
                {isLargeScreen && (saving || questionnaire.questionnaireStatus === 'partial') && (
                    <Flex
                        flexDirection="row"
                        flexWrap="nowrap"
                        alignItems="center"
                        gap="8px"
                        width="120px"
                        color={savingFailed ? 'red' : 'inherit'}
                    >
                        {saving || !savingFailed ? <Save /> : <AlertCircle />}
                        <Text color="inherit">
                            {saving ? t('saving') : savingFailed ? t('error') : t('draftSaved')}
                        </Text>
                    </Flex>
                )}
            </Flex>
            {sortedPages?.[pageIndex] && (
                <QuestionnaireQuestionsForm
                    questionnairePage={sortedPages[pageIndex]}
                    pageIndex={pageIndex}
                    totalPages={questionnaire.pages.length}
                    setSearchParams={setSearchParams}
                    onSubmit={() => {
                        // TODO: Submit questionnaire
                    }}
                />
            )}
        </Flex>
    );
};

export default QuestionnairePage;
